import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

export interface ArticleSection {
  title: string;
  content: string;
  type?: 'text' | 'list' | 'table' | 'proscons' | 'steps';
  items?: string[];
  steps?: { step: number; title: string; description: string }[];
  tableData?: { headers: string[]; rows: string[][] };
  pros?: string[];
  cons?: string[];
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface RelatedArticle {
  title: string;
  slug: string;
}

export interface ArticleData {
  title: string;
  subtitle: string;
  category: string;
  categorySlug: string;
  publishedDate: string;
  updatedDate: string;
  readingTime: string;
  definitionTitle: string;
  definitionContent: string;
  sections: ArticleSection[];
  faqs: FAQItem[];
  relatedArticles: RelatedArticle[];
  articleImage: string;
  imageAlt: string;
}

export default function ArticleLayout({ data }: { data: ArticleData }) {
  return (
    <>
      <Navbar />
      <main>
        <article className="pt-24 pb-16 md:pb-20">
          <div className="px-4 md:px-6 max-w-[800px] mx-auto">
            <nav className="text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-900 cursor-pointer transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/articoli/guida-completa-itaobuy-spreadsheet" className="hover:text-foreground-900 cursor-pointer transition-colors">Guide</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-700 font-medium">{data.title}</span>
            </nav>

            <header className="mb-10">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary-600">{data.category}</span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mt-3 leading-tight">
                {data.title}
              </h1>
              <p className="text-foreground-600 text-sm mt-3 leading-relaxed">{data.subtitle}</p>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-foreground-500">
                <span>Pubblicato: {data.publishedDate}</span>
                <span>|</span>
                <span>Ultimo aggiornamento: {data.updatedDate}</span>
                <span>|</span>
                <span>Tempo di lettura: {data.readingTime}</span>
              </div>
            </header>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={data.articleImage}
                alt={data.imageAlt}
                className="w-full h-[350px] md:h-[420px] object-cover object-center"
              />
            </div>

            <div className="prose-custom">
              <div className="bg-primary-50 border-l-4 border-primary-400 p-4 md:p-5 rounded-r-lg mb-8">
                <p className="text-sm font-medium text-primary-800">
                  <strong>Definizione rapida:</strong> {data.definitionContent}
                </p>
              </div>

              {data.sections.map((section, idx) => (
                <div key={idx}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">{section.title}</h2>

                  {section.type !== 'list' && section.type !== 'steps' && (
                    <p className="text-foreground-700 leading-relaxed mb-4">{section.content}</p>
                  )}

                  {section.type === 'list' && section.items && (
                    <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-6">
                      <p className="text-foreground-700 leading-relaxed mb-4">{section.content}</p>
                      <ul className="space-y-2">
                        {section.items.map((item, i) => (
                          <li key={i} className="flex gap-3 text-sm text-foreground-700">
                            <span className="text-primary-500 mt-0.5 flex-shrink-0">
                              <i className="ri-check-line"></i>
                            </span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {section.type === 'steps' && section.steps && (
                    <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-6">
                      <p className="text-foreground-700 leading-relaxed mb-4">{section.content}</p>
                      <ol className="space-y-4">
                        {section.steps.map((step) => (
                          <li key={step.step} className="flex gap-3">
                            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-xs font-bold">{step.step}</span>
                            <div>
                              <strong className="text-foreground-900">{step.title}</strong>
                              <p className="text-sm text-foreground-600 mt-1">{step.description}</p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {section.type === 'table' && section.tableData && (
                    <div className="overflow-x-auto mb-8">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-background-100">
                            {section.tableData.headers.map((h, i) => (
                              <th key={i} className="text-left p-3 font-semibold text-foreground-900 border-b border-background-200">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.tableData.rows.map((row, i) => (
                            <tr key={i}>
                              {row.map((cell, j) => (
                                <td key={j} className={`p-3 border-b border-background-200 ${j === 0 ? 'text-foreground-700' : j === row.length - 1 ? 'text-accent-600 font-medium' : 'text-foreground-700'}`}>
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {section.type === 'proscons' && (
                    <div className="flex flex-col md:flex-row gap-6 mb-8">
                      <div className="flex-1 bg-primary-50 rounded-xl p-5">
                        <h3 className="font-semibold text-primary-800 mb-2 flex items-center gap-2">
                          <i className="ri-check-line text-primary-600"></i> Vantaggi
                        </h3>
                        <ul className="space-y-2 text-sm text-primary-700">
                          {(section.pros || []).map((p, i) => (
                            <li key={i}>• {p}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex-1 bg-accent-50 rounded-xl p-5">
                        <h3 className="font-semibold text-accent-800 mb-2 flex items-center gap-2">
                          <i className="ri-alert-line text-accent-600"></i> A Cosa Fare Attenzione
                        </h3>
                        <ul className="space-y-2 text-sm text-accent-700">
                          {(section.cons || []).map((c, i) => (
                            <li key={i}>• {c}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">FAQ — Domande Frequenti</h2>
              <div className="space-y-4 mb-8">
                {data.faqs.map((faq, idx) => (
                  <div key={idx} className="bg-background-100 rounded-xl p-5">
                    <h3 className="font-semibold text-foreground-900 mb-2">{faq.question}</h3>
                    <p className="text-sm text-foreground-600">{faq.answer}</p>
                  </div>
                ))}
              </div>

              <div className="mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">Termini Correlati</h2>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700">Taobao</span>
                  <span className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700">Weidian</span>
                  <span className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700">Kaspreadsheet</span>
                  <span className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700">Itaobuy Spreadsheet</span>
                  <span className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700">QC Photos</span>
                  <span className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700">Shipping Agent</span>
                  <span className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700">Marketplace Cinese</span>
                  <span className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700">Shopping Internazionale</span>
                  <span className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700">Prodotti Cinesi</span>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">Articoli Correlati</h2>
                <div className="flex flex-wrap gap-3">
                  {data.relatedArticles.map((rel, idx) => (
                    <Link
                      key={idx}
                      to={`/articoli/${rel.slug}`}
                      className="px-4 py-2.5 text-sm rounded-full bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      {rel.title}
                      <i className="ri-arrow-right-line ml-2 text-xs"></i>
                    </Link>
                  ))}
                  <Link
                    to="/articoli/guida-completa-itaobuy-spreadsheet"
                    className="px-4 py-2.5 text-sm rounded-full bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    Guida Completa
                    <i className="ri-arrow-right-line ml-2 text-xs"></i>
                  </Link>
                </div>
              </div>

              <div className="bg-primary-50 rounded-xl p-6 md:p-8 text-center mt-12">
                <h2 className="text-xl font-heading font-bold text-primary-800 mb-3">
                  Scopri lo Spreadsheet Completo su Kaspreadsheet
                </h2>
                <p className="text-sm text-primary-700 mb-5 max-w-md mx-auto">
                  Esplora tutte le categorie di prodotti verificati e trova le migliori offerte dai marketplace cinesi.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-8 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    itaobuy sheet
                    <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                  <Link
                    to="/"
                    className="px-8 py-3 text-sm font-medium rounded-full border border-primary-300 text-primary-600 hover:bg-primary-100 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Torna alla Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: data.title,
            description: data.subtitle,
            author: {
              '@type': 'Organization',
              name: 'Itaobuy Spreadsheet Team',
            },
            datePublished: '2026-03-01',
            dateModified: data.updatedDate,
            inLanguage: 'it',
          }),
        }}
      />
      {data.faqs && data.faqs.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: data.faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            }),
          }}
        />
      )}
    </>
  );
}